import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MdOutlineTableRestaurant } from "react-icons/md";
import useFirestoreCollection from "../utils/useFirestoreCollection";
import "../Styles/BookNowCTA.css";

/**
 * A single, reusable "Book now" call-to-action.
 *
 * It reads the live `administration` flag itself and renders nothing when
 * reservations are turned off in the admin panel (winter closure or the
 * `bookings` toggle off). This means every placement across the site hides
 * automatically — the gating logic lives in exactly one place.
 *
 * Variants:
 *   - "hero"   large centred button (home hero)
 *   - "inline" centred button inside a section (dishes, about, find us)
 *   - "card"   a standalone panel that fits the home `.cards` grid
 *   - "float"  a fixed button, mobile only (header icon covers desktop)
 *   - "footer" a small icon link matching the footer buttons
 */
export default function BookNowCTA({ variant = "inline", label, className = "" }) {
  const { data: adminData } = useFirestoreCollection("administration");
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (adminData && adminData.length > 0) {
      const admin = adminData[0];
      // Same rule the Header uses: not winter-closed AND bookings toggle on.
      setEnabled(!admin.isWinter && !!admin.bookings);
    }
  }, [adminData]);

  if (!enabled) return null;

  const text = label || (variant === "footer" ? "Book" : "Book a table");

  if (variant === "card") {
    return (
      <Link
        to="/book"
        className={`booknow-card ${className}`.trim()}
        aria-label="Book a table"
      >
        <span className="booknow-card__icon" aria-hidden="true">
          <MdOutlineTableRestaurant size={36} />
        </span>
        <span className="booknow-card__eyebrow">Reservations</span>
        <span className="booknow-card__title">Reserve a table</span>
        <span className="booknow-card__cta">Book now</span>
      </Link>
    );
  }

  if (variant === "footer") {
    return (
      <Link
        to="/book"
        className={`footer_icon_btn ${className}`.trim()}
        aria-label="Book a table"
        title="Book a table"
      >
        <MdOutlineTableRestaurant className="footer_buttons" size={22} />
      </Link>
    );
  }

  if (variant === "float") {
    return (
      <Link
        to="/book"
        className={`booknow-float ${className}`.trim()}
        aria-label="Book a table"
      >
        <MdOutlineTableRestaurant size={20} aria-hidden="true" />
        <span>{text}</span>
      </Link>
    );
  }

  // hero + inline share the pill button, differing only in spacing.
  return (
    <div className={`booknow-wrap booknow-wrap--${variant} ${className}`.trim()}>
      <Link
        to="/book"
        className="md-button md-button--filled md-state-layer booknow-btn"
        aria-label="Book a table"
      >
        <MdOutlineTableRestaurant size={20} aria-hidden="true" /> {text}
      </Link>
    </div>
  );
}
